import React, { useState, useEffect } from "react";
import SidebarTopbar from "../../components/layouts/layout";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { HiOutlineDocumentText, HiOutlineDocumentArrowUp } from "react-icons/hi2";
import "./uploadfile.css";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

function PubChatUpload() {
  const [alerts, setAlerts] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [publications, setPublications] = useState([]);
  const [isLoadingPublications, setIsLoadingPublications] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [chatStatus, setChatStatus] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const fetchChatHistory = async (pubId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/chat_history?file_id=${pubId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        const data = await response.json();
        return data.length > 0;
      }
    } catch (err) {
      console.error("Error fetching chat history:", err);
    }
    return false;
  };

  // Fetch publications for history
  useEffect(() => {
    const fetchPublications = async () => {
      setIsLoadingPublications(true);
      try {
        const response = await fetch(
          `${BASE_URL}/getPublicationsWithFilesAndTasks`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();

        let publicationData = [];
        if (Array.isArray(data)) {
          publicationData = data;
        } else if (data && typeof data === "object") {
          publicationData = [data];
        }

        // Sort by id descending (newest first)
        publicationData.sort((a, b) => b.id - a.id);
        setPublications(publicationData);
        setIsLoadingPublications(false);

        // Fetch chat status for each publication (in background)
        const statusMap = {};
        for (const pub of publicationData) {
          statusMap[pub.id] = await fetchChatHistory(pub.id);
        }
        setChatStatus(statusMap);
      } catch (error) {
        console.error("Error fetching publications:", error);
        setIsLoadingPublications(false);
      }
    };

    fetchPublications();
  }, [BASE_URL, token]);

  const closeAlert = (id) => {
    setAlerts((a) => a.filter((alert) => alert.id !== id));
  };

  const createAlert = (message, severity) => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => closeAlert(id), 5000);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) {
      createAlert("File size is too large. Please upload a file below 20MB.", "warning");
      return;
    }

    setSelectedFile(file);
    handleUpload(file);
  };

  const handleUpload = async (file) => {
    if (!file) {
      createAlert("Please select a file first.", "warning");
      return;
    }

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    if (!token) {
      alert("You need to log in first to get a token.");
      setIsUploading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/uploadPublication`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.status === 413) {
        createAlert("File size is too large. Please upload a file below 20MB.", "warning");
      } else if (response.status === 500) {
        createAlert("Sorry it's us. Rectifying. Please try after some time.", "error");
      } else if (response.status === 400) {
        createAlert("Oops! This doesn’t look like a medical publication.To ensure accurate analysis, please upload a medical or scientific publication. Try again!", "error");
      } else if (response.ok) {
        const data = await response.json();
        createAlert("File uploaded successfully!", "success");

        // Start chat initialization
        const chatFormData = new FormData();
        chatFormData.append("publicationId", data.id);

        try {
          await fetch(`${BASE_URL}/start_chat`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: chatFormData,
          });
        } catch (err) {
          console.error("Error during chat initialization:", err);
        }

        // Generate graphs
        const graphFormData = new FormData();
        graphFormData.append("publication_id", data.id);

        try {
          await fetch(`${BASE_URL}/generateGraphs`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: graphFormData,
          });
        } catch (err) {
          console.error("Error generating graphs:", err);
        }

        // Navigate to chat
        setSelectedFile(null);
        navigate(`/pubChat/${data.id}`);
      } else {
        createAlert("Upload failed. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during file upload:", error);
      createAlert("Error occurred. Please try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = (e) => {
    if (isUploading) return;
    e.stopPropagation();
    document.getElementById("dialogFileInput").click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (droppedFile.size > 20 * 1024 * 1024) {
        createAlert("File size is too large. Please upload a file below 20MB.", "warning");
        return;
      }
      setSelectedFile(droppedFile);
      handleUpload(droppedFile);
    }
  };

  const handleNavigateToPubChat = (pubId) => {
    navigate(`/pubChat/${pubId}`);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
  };

  return (
    <SidebarTopbar>
      <div className="pubchat-upload-wrapper">
        {alerts.length > 0 && (
          <div
            style={{
              position: "fixed",
              top: "70px",
              right: "20px",
              width: "300px",
              zIndex: 1100,
            }}
          >
            <Stack spacing={2}>
              {alerts.map((alert) => (
                <Alert
                  key={alert.id}
                  severity={alert.severity}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => closeAlert(alert.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  {alert.message}
                </Alert>
              ))}
            </Stack>
          </div>
        )}

        {/* Header Section with Embedded Upload */}
        <div className="pubchat-header-section">
          <div className="pubchat-header-left">
            <div className="pubchat-header-text">
              <h2>Upload Publication</h2>
              <p>Get concise, citation-linked answers, summaries, and follow-ups from your publications instantly.</p>
            </div>
          </div>
          <div className="pubchat-header-right">
            <input
              type="file"
              id="dialogFileInput"
              accept=".pdf,.docx"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              disabled={isUploading}
            />
            
            {isUploading ? (
              <button className="pubchat-extracting-btn" disabled>
                <span className="upload-spinner"></span>
                Extracting your publication. Please hold on...
              </button>
            ) : (
              <div
                className={`pubchat-dropzone ${isDragging ? "dragging" : ""}`}
                onClick={triggerFileInput}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="dropzone-content">
                  <div className="dropzone-top-row">
                    <HiOutlineDocumentArrowUp className="dropzone-upload-icon" />
                    <span className="dropzone-label">Upload PDF</span>
                  </div>
                  <span className="allowed-formats">Allowed formats: PDF, DOCX | Max: 20MB</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Publication History Section */}
        <h3 className="history-title-outside">Publication Chat History</h3>
        <div className="pubchat-history-section">
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Publication Name</th>
                  <th>Uploaded Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  if (isLoadingPublications) {
                    return (
                      <tr>
                        <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                          <span className="upload-spinner" style={{ display: "inline-block", marginRight: "10px" }}></span>
                          Loading publications...
                        </td>
                      </tr>
                    );
                  }
                  
                  const totalPages = Math.ceil(publications.length / itemsPerPage);
                  const start = (currentPage - 1) * itemsPerPage;
                  const currentSlice = publications.slice(start, start + itemsPerPage);
                  
                  return currentSlice.length > 0 ? (
                    currentSlice.map((pub) => (
                      <tr key={pub.id}>
                        <td className="pub-name-cell" title={pub.title}>
                          {pub.title}
                        </td>
                        <td>{pub.date || "-"}</td>
                        <td>
                          <button
                            className={`chat-button ${chatStatus[pub.id] ? "view" : "start"}`}
                            onClick={() => handleNavigateToPubChat(pub.id)}
                          >
                            {chatStatus[pub.id] ? "View Chat" : "Start Chat"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" style={{ textAlign: "center", padding: "20px" }}>
                        No publications found
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
          
        {/* Pagination - Outside the card */}
        {publications.length > itemsPerPage && (
          <div className="pagination">
            <button
              className="page-button"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              <FaChevronLeft />
            </button>
            {(() => {
              const totalPages = Math.ceil(publications.length / itemsPerPage);
              const maxPagesToShow = 3;
              let startPage = Math.max(1, Math.min(currentPage - 1, totalPages - maxPagesToShow + 1));
              startPage = Math.max(1, startPage);
              const endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);
              const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
              
              return pageNumbers.map((num) => (
                <button
                  key={num}
                  className={`page-button ${num === currentPage ? "active" : ""}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ));
            })()}
            <button
              className="page-button"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, Math.ceil(publications.length / itemsPerPage)))}
              disabled={currentPage === Math.ceil(publications.length / itemsPerPage)}
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>
    </SidebarTopbar>
  );
}

export default PubChatUpload;
